import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ChevronDown, ChevronUp, Swords, CheckCircle2, GraduationCap, Award, Building2, CalendarRange, MapPin } from 'lucide-react';
import { experience, education } from '../../data/mock';
import { Badge } from '../ui/badge';

const questTypeStyles = {
  ACTIVE_QUEST: { label: 'ACTIVE QUEST', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', dot: 'bg-emerald-400' },
  COMPLETED: { label: 'COMPLETED', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', dot: 'bg-cyan-400' },
};

function QuestCard({ quest, index }) {
  const [expanded, setExpanded] = useState(index === 0);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const style = questTypeStyles[quest.type];
  const Icon = quest.questIcon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -30 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative pl-8 pb-10 last:pb-0"
    >
      {/* Timeline line */}
      {index < experience.length - 1 && (
        <div className="absolute left-[11px] top-8 bottom-0 w-px bg-gradient-to-b from-[#1a1a2e] to-transparent" />
      )}

      {/* Timeline dot */}
      <div className={`absolute left-0 top-1 w-[23px] h-[23px] rounded-full border-2 ${style.border} ${style.bg} flex items-center justify-center`}>
        <div className={`w-2 h-2 rounded-full ${style.dot} ${quest.type === 'ACTIVE_QUEST' ? 'animate-pulse' : ''}`} />
      </div>

      {/* Card */}
      <div
        className="rounded-xl border border-[#1a1a2e] bg-[#0a0a14]/60 hover:border-[#252540] transition-colors cursor-pointer overflow-hidden"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <Badge variant="outline" className={`${style.bg} ${style.border} ${style.color} font-mono text-[10px] px-2 py-0.5`}>
                  {style.label}
                </Badge>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#111120] border border-[#1a1a2e] flex items-center justify-center shrink-0">
                  <Icon size={18} className={style.color} />
                </div>
                <div>
                  <h3 className="font-semibold text-zinc-100 text-base" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    {quest.title}
                  </h3>
                  <p className="font-mono text-sm text-zinc-500">{quest.company}</p>
                </div>
              </div>
              <p className="text-zinc-400 text-sm mt-3 leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>
                {quest.description}
              </p>
              <div className="grid gap-3 sm:grid-cols-3 mt-4">
                <div className="rounded-lg border border-[#1a1a2e] bg-[#0d0d16] px-3 py-2">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">Company</span>
                  <div className="mt-1 flex items-center gap-2 text-sm text-zinc-300">
                    <Building2 size={13} className="text-zinc-500 shrink-0" />
                    <span>{quest.company}</span>
                  </div>
                </div>
                <div className="rounded-lg border border-[#1a1a2e] bg-[#0d0d16] px-3 py-2">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">Location</span>
                  <div className="mt-1 flex items-center gap-2 text-sm text-zinc-300">
                    <MapPin size={13} className="text-zinc-500 shrink-0" />
                    <span>{quest.location}</span>
                  </div>
                </div>
                <div className="rounded-lg border border-[#1a1a2e] bg-[#0d0d16] px-3 py-2">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">Period</span>
                  <div className="mt-1 flex items-center gap-2 text-sm text-zinc-300">
                    <CalendarRange size={13} className="text-zinc-500 shrink-0" />
                    <span>{quest.period}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <span className="font-mono text-xs text-amber-400 bg-amber-500/10 border border-amber-500/15 rounded px-2 py-1">
                +{quest.xpReward} XP
              </span>
              {expanded ? <ChevronUp size={16} className="text-zinc-600" /> : <ChevronDown size={16} className="text-zinc-600" />}
            </div>
          </div>
        </div>

        {/* Expanded content */}
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="border-t border-[#1a1a2e] px-5 py-4"
          >
            <div className="mb-4">
              <span className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider">Highlights</span>
              <ul className="mt-2 space-y-2">
                {quest.achievements.map((ach, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                    {ach}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <span className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider">Core Stack</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {quest.techStack.map((tech) => (
                  <span key={tech} className="px-2.5 py-1 rounded-md bg-[#111120] border border-[#1a1a2e] font-mono text-xs text-zinc-400">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default function QuestLog() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="quests" className="relative py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-3">
            <Swords size={20} className="text-emerald-400" />
            <span className="font-mono text-xs text-emerald-400/60 uppercase tracking-widest">// Quest Log</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-zinc-100" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Experience
          </h2>
          <p className="text-zinc-500 mt-2 text-base max-w-lg" style={{ fontFamily: 'Figtree, sans-serif' }}>
            A clearer view of role scope, timeline, and measurable outcomes across each stage of the journey.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {experience.map((quest, i) => (
            <QuestCard key={quest.id} quest={quest} index={i} />
          ))}
        </div>

        {/* Education */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="mt-16 rounded-xl border border-[#1a1a2e] bg-[#0a0a14]/60 p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
              <GraduationCap size={18} className="text-indigo-400" />
            </div>
            <div>
              <span className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider">Education</span>
              <h3 className="font-semibold text-zinc-200 text-sm" style={{ fontFamily: 'Figtree, sans-serif' }}>
                {education.degree}
              </h3>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-mono text-[10px] text-zinc-600">INSTITUTION</span>
              <p className="text-zinc-400 mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>{education.institution}</p>
            </div>
            <div>
              <span className="font-mono text-[10px] text-zinc-600">LOCATION</span>
              <p className="text-zinc-400 mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>{education.location}</p>
            </div>
            <div>
              <span className="font-mono text-[10px] text-zinc-600">PERIOD</span>
              <p className="text-zinc-400 mt-1 font-mono text-xs">{education.period}</p>
            </div>
            <div>
              <span className="font-mono text-[10px] text-zinc-600">ACHIEVEMENT</span>
              <div className="flex items-center gap-2 mt-1">
                <Award size={14} className="text-amber-400" />
                <p className="text-zinc-400" style={{ fontFamily: 'Figtree, sans-serif' }}>{education.achievement} — {education.grade}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
